import { useEffect, useMemo, useRef, useState, KeyboardEvent } from "react";

import { useFlags } from "launchdarkly-react-client-sdk";

import { ContentCard } from "sj-ui-toolkit/src/atlas/components";
import type { ContentCardProps } from "sj-ui-toolkit/src/atlas/components/ContentCard";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts/User/UserContextProvider";

import S from "./CatalogResults.module.scss";

// Tab filter types
const FILTER_TABS = { ALL: "all", PATHS: "paths", QUESTS: "quests", LESSONS: "lessons" } as const;

type TabKey = (typeof FILTER_TABS)[keyof typeof FILTER_TABS];

type CategorizedCard = ContentCardProps & { cardType: "path" | "quest" | "lesson" };

interface CatalogResultsProps {
  isLoading: boolean;
  results?: ContentCardProps[];
  noResults: boolean;
  isAISkillsLabSelected?: boolean;
}

const CatalogResults = ({
  isLoading,
  results = [],
  noResults,
  isAISkillsLabSelected = false,
}: CatalogResultsProps) => {
  const [activeTab, setActiveTab] = useState<TabKey>(FILTER_TABS.ALL);
  const tabRefs = useRef<Partial<Record<TabKey, HTMLButtonElement | null>>>({});
  const { customPathCatalogFilters } = useFlags();
  const { isRestrictedBundle } = useUserContext();

  const hideQuests = isRestrictedBundle;

  // When quests are hidden for the tenant's bundle, drop them from the result set entirely so
  // they can't leak through the "All" tab (which renders the full result list).
  const visibleResults = useMemo(
    () => (hideQuests ? results.filter(item => item.contentType !== "quest") : results),
    [hideQuests, results]
  );

  const TAB_LIST = useMemo(() => {
    const tabs: { key: TabKey; label: string }[] = [
      { key: FILTER_TABS.ALL, label: "All" },
      { key: FILTER_TABS.PATHS, label: "Paths" },
    ];

    if (customPathCatalogFilters && !hideQuests) {
      tabs.push({ key: FILTER_TABS.QUESTS, label: "Quests" });
    }

    tabs.push({ key: FILTER_TABS.LESSONS, label: "Lessons" });

    return tabs;
  }, [customPathCatalogFilters, hideQuests]);

  useEffect(() => {
    const isActiveTabAvailable = TAB_LIST.some(tab => tab.key === activeTab);
    if (!isActiveTabAvailable) {
      setActiveTab(FILTER_TABS.ALL);
    }
  }, [TAB_LIST, activeTab]);

  // Categorize results
  const categorizedResults = useMemo(() => {
    const paths: CategorizedCard[] = [];
    const quests: CategorizedCard[] = [];
    const lessons: CategorizedCard[] = [];
    visibleResults.forEach(item => {
      if (item.contentType === "path") {
        paths.push({ ...item, cardType: "path" });
      } else if (item.contentType === "quest") {
        quests.push({ ...item, cardType: "quest" });
      } else {
        lessons.push({ ...item, cardType: "lesson" });
      }
    });

    return { paths, quests, lessons };
  }, [visibleResults]);

  // Get filtered results based on active tab
  const filteredResults = useMemo(() => {
    switch (activeTab) {
      case FILTER_TABS.PATHS:
        return categorizedResults.paths;
      case FILTER_TABS.QUESTS:
        return categorizedResults.quests;
      case FILTER_TABS.LESSONS:
        return categorizedResults.lessons;
      case FILTER_TABS.ALL:
      default:
        return visibleResults;
    }
  }, [activeTab, visibleResults, categorizedResults]);

  // Count for each tab
  const tabCounts = useMemo<Record<TabKey, number>>(
    () => ({
      all: visibleResults.length,
      paths: categorizedResults.paths.length,
      quests: categorizedResults.quests.length,
      lessons: categorizedResults.lessons.length,
    }),
    [visibleResults, categorizedResults]
  );

  // Keyboard navigation for tabs (arrow keys)
  const handleTabKeyDown = (e: KeyboardEvent<HTMLButtonElement>, tabKey: TabKey) => {
    const currentIndex = TAB_LIST.findIndex(t => t.key === tabKey);
    let nextIndex;

    if (e.key === "ArrowRight") {
      e.preventDefault();
      nextIndex = (currentIndex + 1) % TAB_LIST.length;
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      nextIndex = (currentIndex - 1 + TAB_LIST.length) % TAB_LIST.length;
    } else if (e.key === "Home") {
      e.preventDefault();
      nextIndex = 0;
    } else if (e.key === "End") {
      e.preventDefault();
      nextIndex = TAB_LIST.length - 1;
    } else {
      return;
    }

    const nextTab = TAB_LIST[nextIndex];
    setActiveTab(nextTab.key);
    tabRefs.current[nextTab.key]?.focus();
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={S.resultsContainer}>
        <div className={S.loadingState} role="status" aria-live="polite">
          <i className="pi pi-spin pi-spinner" aria-hidden="true" />
          <span>Searching catalog...</span>
        </div>
      </div>
    );
  }

  // No results state
  if (noResults) {
    return (
      <div className={S.resultsContainer}>
        <div className={S.noResults} role="status" aria-live="polite">
          <i className="pi pi-search" aria-hidden="true" />
          {isAISkillsLabSelected ? (
            <>
              <h3>Not available for your organization</h3>
              <p>
                If you would like to enable these lessons, please reach out to your Account Manager.
              </p>
            </>
          ) : (
            <>
              <h3>No results found</h3>
              <p>Try adjusting your search terms or filters to find what you're looking for.</p>
            </>
          )}
        </div>
      </div>
    );
  }

  // Empty results (initial state, cleared, or all filtered out for the tenant's bundle)
  if (visibleResults.length === 0) {
    return null;
  }

  return (
    <div className={S.resultsContainer}>
      <h2 className="sr-only">Search Results</h2>
      {/* Tabs Header */}
      <div className={S.tabsHeader} role="tablist" aria-label="Result type filters">
        {TAB_LIST.map(tab => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              ref={el => (tabRefs.current[tab.key] = el)}
              role="tab"
              aria-selected={isActive}
              aria-controls="catalog-results-tabpanel"
              id={`catalog-tab-${tab.key}`}
              tabIndex={isActive ? 0 : -1}
              className={`${S.tab} ${isActive ? S.active : ""}`}
              onClick={() => setActiveTab(tab.key)}
              onKeyDown={e => handleTabKeyDown(e, tab.key)}
            >
              {tab.label.toUpperCase()}{" "}
              <span className={S.tabCount} aria-label={`${tabCounts[tab.key]} results`}>
                {tabCounts[tab.key]}
              </span>
            </button>
          );
        })}
      </div>

      {/* Results Grid */}
      <div
        id="catalog-results-tabpanel"
        role="tabpanel"
        aria-labelledby={`catalog-tab-${activeTab}`}
        aria-live="polite"
      >
        <div className={S.resultsGrid}>
          {filteredResults.map((item, index) => (
            <ContentCard key={item.collectionKey || index} {...item} catalogView={true} />
          ))}
        </div>

        {/* Empty filtered state */}
        {filteredResults.length === 0 && (
          <div className={S.emptyFilterState}>
            <p>No {activeTab} found matching your criteria.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CatalogResults;
